/**
 * Browser storage at sign-out and at sign-in.
 *
 * Sign-out (`clearSignedInStorage`) empties session storage, which holds
 * only hand-offs and per-tab memories, and removes every localStorage key
 * except the kept view preferences (src/lib/keptAcrossSignOut.ts), the
 * note drafts (each stamped with its author) and the cross-tab signal key.
 *
 * Sign-in (`claimStorageFor`) makes the store the signed-in person's: when
 * the last person recorded is someone else, or nobody, the same clear
 * runs; on every sign-in another author's drafts are removed and
 * unstamped ones take this person's id; then the signal key records who
 * is signed in.
 *
 * The signal key is how other open tabs follow: removing or changing it
 * fires a `storage` event there (src/lib/pageRestoreGuard.ts). It never
 * decides whose a draft is; the draft's own stamp does.
 *
 * Every access is inside try/catch: a browser that refuses storage has
 * nothing to clear.
 */

import { claimDraftsFor, isDraftKey } from '@/lib/draftAutosave';
import { isKeptAcrossSignOut } from '@/lib/keptAcrossSignOut';

export const SIGNED_IN_USER_KEY = 'olive:signed-in-user';

/** The stores to work on; each defaults to the window's, and null means none. */
export interface SignedInStores {
  local?: Storage | null;
  session?: Storage | null;
}

function localOf(stores: SignedInStores): Storage | null {
  if (stores.local !== undefined) return stores.local;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function sessionOf(stores: SignedInStores): Storage | null {
  if (stores.session !== undefined) return stores.session;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Every key, read into an array before any is removed: removing while
 *  walking by index skips every other key. */
function keysOf(storage: Storage): string[] {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key !== null) keys.push(key);
  }
  return keys;
}

export function clearSessionStorage(stores: SignedInStores = {}): void {
  try {
    const session = sessionOf(stores);
    if (session === null) return;
    for (const key of keysOf(session)) session.removeItem(key);
  } catch {
    // storage refused: nothing to clear
  }
}

export function clearSignedInStorage(stores: SignedInStores = {}): void {
  clearSessionStorage(stores);
  try {
    const local = localOf(stores);
    if (local === null) return;
    for (const key of keysOf(local)) {
      if (isKeptAcrossSignOut(key) || isDraftKey(key) || key === SIGNED_IN_USER_KEY) continue;
      local.removeItem(key);
    }
  } catch {
    // storage refused: nothing to clear
  }
}

export function forgetSignedInUser(stores: SignedInStores = {}): void {
  try {
    localOf(stores)?.removeItem(SIGNED_IN_USER_KEY);
  } catch {
    // storage refused: nothing to remove
  }
}

export function claimStorageFor(userId: string, stores: SignedInStores = {}): void {
  try {
    const local = localOf(stores);
    if (local === null) {
      clearSessionStorage(stores);
      return;
    }
    if (local.getItem(SIGNED_IN_USER_KEY) !== userId) clearSignedInStorage(stores);
    claimDraftsFor(userId, local);
    local.setItem(SIGNED_IN_USER_KEY, userId);
  } catch {
    // storage refused: nothing to claim
  }
}
